import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { ShopperInsightsSearchHitCheckout } from "lib/types/generated/graphql-types"
import Money from "components/Money"
import DineroFactory from "dinero.js"
import dayjs from "dayjs"
import { getCountryName } from "helpers/countries"
import { SmallInfo } from "./CustomerOrderTable.styled"
import CheckoutStatus from "./CheckoutStatus"
import OrderLineRow from "./OrderLineRow"
import { ReactComponent as DownIcon } from "images/icons/chevron-down.svg"
import {
  TableRow,
  CheckoutCell,
  Section,
  DateTime,
  TotalAmount,
  ItemCount,
  OrderLinesContainer,
  OrderLinesHeader,
  OrderLinesTableHeader,
  CheckoutRowContainer,
  ExpandButtonStyled,
  OrderReferenceContainer,
  ConfirmedOrderReference,
  UnconfirmedOrderReference,
  CheckoutIdContainer,
  CopyIconStyled
} from "./CustomerCheckoutSessionTable.styled"
import { CustomerOrders } from "../../classes/customer/CustomerOrders"

type Props = {
  checkout: ShopperInsightsSearchHitCheckout
  customerOrders: CustomerOrders
}

const CustomerCheckoutSessionRow = ({ checkout, customerOrders }: Props) => {
  const navigate = useNavigate()
  const [showOrderLines, setShowOrderLines] = useState(false)
  const total = checkout.items?.reduce((sum, item) => sum + (item.totalPriceAmount || 0), 0) || 0
  const isConfirmed = checkout.isConfirmed

  return (
    <>
      <TableRow
        style={{ borderTop: "none", cursor: "pointer", backgroundColor: "#f8f9fa" }}
        onClick={() => setShowOrderLines(!showOrderLines)}
      >
        <CheckoutCell style={{ paddingLeft: "3rem" }}>
          <CheckoutRowContainer>
            <ExpandButtonStyled $expanded={showOrderLines} $padding={"0 1rem 0"}>
              <DownIcon />
            </ExpandButtonStyled>
            <div>
              <OrderReferenceContainer>
                {isConfirmed ? (
                  <ConfirmedOrderReference
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/orders/${checkout.orderId}`)
                    }}
                  >
                    #{checkout.orderReference}
                  </ConfirmedOrderReference>
                ) : (
                  <UnconfirmedOrderReference>#{checkout.orderReference}</UnconfirmedOrderReference>
                )}
                <CopyIconStyled
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    navigator.clipboard.writeText(checkout.orderReference)
                  }}
                />
              </OrderReferenceContainer>
              <SmallInfo>
                <CheckoutIdContainer>
                  CheckoutId
                  <CopyIconStyled
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      navigator.clipboard.writeText(checkout.id)
                    }}
                  />
                </CheckoutIdContainer>
              </SmallInfo>
            </div>

            <DateTime>
              <div>
                <span>
                  <b>{dayjs(checkout.created).format("YYYY-MM-DD")}</b>
                </span>
                <span>{dayjs(checkout.created).format("HH:mm")}</span>
              </div>
            </DateTime>
          </CheckoutRowContainer>
        </CheckoutCell>
        <div></div>
        <Section className="hideOnMediumScreens">{customerOrders?.getStoreGroupName(checkout.storeGroupId)}</Section>

        <Section className={"hideOnMediumScreens"}>{getCountryName(checkout.countryCode)}</Section>

        <Section className="hideOnSmallScreens">
          <div>
            <TotalAmount>
              <Money
                amount={total}
                currencyUnit={checkout.currencyCode as DineroFactory.Currency}
              />
            </TotalAmount>
            <ItemCount>{checkout.items?.length || 0} items</ItemCount>
          </div>
        </Section>

        <CheckoutStatus checkout={checkout} />
      </TableRow>

      {showOrderLines && checkout.items && checkout.items.length > 0 && (
        <OrderLinesContainer>
          <OrderLinesHeader>Order Lines ({checkout.items.length} items)</OrderLinesHeader>
          <OrderLinesTableHeader>
            <div style={{ paddingLeft: "1rem" }}>Product</div>
            <div style={{ textAlign: "center" }}>Qty</div>
            <div style={{ textAlign: "right" }}>Unit Price</div>
            <div style={{ textAlign: "right" }}>Total</div>
            <div></div>
          </OrderLinesTableHeader>
          {checkout.items.map((item) => (
            <OrderLineRow key={item.id} item={item} currencyCode={checkout.currencyCode} />
          ))}
        </OrderLinesContainer>
      )}
    </>
  )
}

export default CustomerCheckoutSessionRow
